import { io } from 'socket.io-client';
import { getLoggedInUser } from 'utilities/helpers';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.notificationListeners = [];
    this.pendingNotificationListeners = [];
    this.connectionStatusListeners = [];
    this.unreadNotificationsListeners = [];
    this.readNotificationsListeners = []; // New property for read notifications listeners
    this.apiUrl =
      process.env.REACT_APP_API_URL2 || 'https://elite-api.lexyslabs.com/';
  }

  // Initialize the socket connection
  initialize() {
    const secretPath = process.env.REACT_APP_SOCKET_PATH || 'socket-screct';
    // Get user data from your existing helper
    // console.log('Before socket connection. Config:', {
    //   apiUrl: this.apiUrl,
    //   path: `/${secretPath}`
    // });
    this.socket = io(this.apiUrl, {
      path: `/${secretPath}`,
      transports: ['polling'], // Force polling only (no WebSocket)
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      autoConnect: false,
      auth: {
        token: localStorage.getItem('accessToken') // Assuming you store token in localStorage
      }
    });
    console.log('Socket initialized:', this.socket);
    // Set up event listeners
    this.socket.on('connect', this.handleConnect.bind(this));
    this.socket.on('disconnect', this.handleDisconnect.bind(this));
    this.socket.on('connect_error', this.handleError.bind(this));
    this.socket.on('notification', this.handleNotification.bind(this));
    this.socket.on(
      'pendingNotifications',
      this.handlePendingNotifications.bind(this)
    );
    this.socket.on(
      'unreadNotificationsResponse',
      this.handleUnreadNotifications.bind(this)
    );
    this.socket.on(
      'readNotificationsResponse', // New event listener for read notifications
      this.handleReadNotifications.bind(this)
    );
    this.socket.on('error', this.handleSocketError.bind(this));

    return this;
  }

  // Connect to the socket server
  connect() {
    if (!this.socket) {
      console.error('Socket not initialized. Call initialize() first.');
      return;
    }
    this.socket.connect();
  }

  onConnect(callback) {
    if (this.socket) {
      this.socket.on('connect', callback);
    }
  }

  // Get unread notifications
  getUnreadNotifications() {
    if (!this.isConnected) {
      console.warn(
        'Socket not connected. Will get notifications when connected.'
      );
      return Promise.resolve([]);
    }

    const { user_details } = getLoggedInUser();
    const userId = user_details?.profile_id;

    if (!userId) {
      console.error('User ID not available for unread notifications');
      return Promise.resolve([]);
    }

    // Request unread notifications
    this.socket.emit('unreadNotifications', { userId });

    // Return promise that resolves with notifications
    return new Promise((resolve) => {
      const handler = (notifications) => {
        this.socket.off('unreadNotificationsResponse', handler);
        resolve(notifications);
      };

      this.socket.on('unreadNotificationsResponse', handler);

      // Set timeout in case server doesn't respond
      setTimeout(() => {
        this.socket.off('unreadNotificationsResponse', handler);
        resolve([]);
      }, 5000);
    });
  }

  // Get read notifications (new method)
  getReadNotifications() {
    if (!this.isConnected) {
      console.warn(
        'Socket not connected. Will get read notifications when connected.'
      );
      return Promise.resolve([]);
    }

    const { user_details } = getLoggedInUser();
    const userId = user_details?.profile_id;

    if (!userId) {
      console.error('User ID not available for read notifications');
      return Promise.resolve([]);
    }

    // Request read notifications
    this.socket.emit('readNotifications', { userId });

    // Return promise that resolves with notifications
    return new Promise((resolve) => {
      const handler = (notifications) => {
        this.socket.off('readNotificationsResponse', handler);
        resolve(notifications);
      };

      this.socket.on('readNotificationsResponse', handler);

      // Set timeout in case server doesn't respond
      setTimeout(() => {
        this.socket.off('readNotificationsResponse', handler);
        resolve([]);
      }, 5000);
    });
  }

  // Register user with socket server
  registerUser() {
    if (!this.isConnected) {
      console.warn('Socket not connected. Will register when connected.');
      return;
    }

    const { user_details } = getLoggedInUser();
    const userId = user_details?.profile_id;
    if (!userId) {
      console.error('User ID not available for socket registration');
      return;
    }

    const deviceInfo = {
      platform: navigator.platform,
      userAgent: navigator.userAgent
    };

    this.socket.emit('registerUser', { userId, deviceInfo });
    console.log('Registered user with socket:', userId);
  }

  // Mark notifications as read
  markNotificationsRead(notificationIds) {
    if (!this.isConnected) {
      console.warn('Socket not connected. Cannot mark notifications as read.');
      return;
    }

    const { user_details } = getLoggedInUser();
    const userId = user_details?.profile_id;
    if (!userId) {
      console.error('User ID not available');
      return;
    }

    this.socket.emit('markNotificationsRead', { userId, notificationIds });

    // Also update localStorage notifications
    const storedNotifications =
      JSON.parse(localStorage.getItem('notifications')) || [];
    const updatedNotifications = storedNotifications.map((notification) => {
      if (notificationIds.includes(notification.id)) {
        return { ...notification, read: true };
      }
      return notification;
    });

    localStorage.setItem('notifications', JSON.stringify(updatedNotifications));

    // Notify listeners about the update
    this.notificationListeners.forEach((listener) =>
      listener(updatedNotifications)
    );
  }

  // Add a notification listener
  addNotificationListener(callback) {
    this.notificationListeners.push(callback);

    // Immediately notify with stored notifications
    const storedNotifications =
      JSON.parse(localStorage.getItem('notifications')) || [];
    callback(storedNotifications);

    return () => this.removeNotificationListener(callback);
  }

  // Remove a notification listener
  removeNotificationListener(callback) {
    this.notificationListeners = this.notificationListeners.filter(
      (listener) => listener !== callback
    );
  }

  // Add a pending notification listener
  addPendingNotificationListener(callback) {
    this.pendingNotificationListeners.push(callback);
    return () => this.removePendingNotificationListener(callback);
  }

  // Remove a pending notification listener
  removePendingNotificationListener(callback) {
    this.pendingNotificationListeners =
      this.pendingNotificationListeners.filter(
        (listener) => listener !== callback
      );
  }

  // Add a unread notification listener
  addUnreadNotificationsListener(callback) {
    this.unreadNotificationsListeners.push(callback);
    return () => this.removeUnreadNotificationsListener(callback);
  }

  // Remove a unread notification listener
  removeUnreadNotificationsListener(callback) {
    this.unreadNotificationsListeners =
      this.unreadNotificationsListeners.filter(
        (listener) => listener !== callback
      );
  }

  // Add a read notification listener (new method)
  addReadNotificationsListener(callback) {
    this.readNotificationsListeners.push(callback);
    return () => this.removeReadNotificationsListener(callback);
  }

  // Remove a read notification listener (new method)
  removeReadNotificationsListener(callback) {
    this.readNotificationsListeners = this.readNotificationsListeners.filter(
      (listener) => listener !== callback
    );
  }

  // Add a connection status listener
  addConnectionStatusListener(callback) {
    this.connectionStatusListeners.push(callback);
    return () => this.removeConnectionStatusListener(callback);
  }

  // Remove a connection status listener
  removeConnectionStatusListener(callback) {
    this.connectionStatusListeners = this.connectionStatusListeners.filter(
      (listener) => listener !== callback
    );
  }

  // Handle socket connection
  handleConnect() {
    console.log('Socket connected');
    this.isConnected = true;
    this.registerUser(); // Auto-register user on connect

    // Request unread notifications after connecting
    const { user_details } = getLoggedInUser();
    const userId = user_details?.profile_id;
    if (userId) {
      this.socket.emit('unreadNotifications', { userId });
      // Also request read notifications on connect
      this.socket.emit('readNotifications', { userId });
    }

    this.connectionStatusListeners.forEach((listener) => listener(true));
  }

  // Handle socket disconnection
  handleDisconnect() {
    console.log('Socket disconnected');
    this.isConnected = false;
    this.connectionStatusListeners.forEach((listener) => listener(false));
  }

  // Handle connection error
  handleError(error) {
    console.error('Socket connection error:', error);
    this.isConnected = false;
    this.connectionStatusListeners.forEach((listener) =>
      listener(false, error)
    );
  }

  // Handle incoming notifications
  handleNotification(notification) {
    console.log('New notification received:', notification);

    // Add timestamp
    const notificationWithTimestamp = {
      ...notification,
      receivedAt: new Date(),
      read: false
    };

    // Get existing notifications from localStorage
    const storedNotifications =
      JSON.parse(localStorage.getItem('notifications')) || [];

    // Add the new notification to the list
    const updatedNotifications = [
      notificationWithTimestamp,
      ...storedNotifications
    ];

    // Save to localStorage
    localStorage.setItem('notifications', JSON.stringify(updatedNotifications));

    // Notify listeners
    this.notificationListeners.forEach((listener) =>
      listener(updatedNotifications)
    );
  }

  // Handle pending notifications
  handlePendingNotifications(notifications) {
    console.log('Pending notifications received:', notifications);

    // Format notifications
    const formattedNotifications = notifications.map((notification) => ({
      ...notification,
      receivedAt: new Date(),
      read: false
    }));

    // Get existing notifications from localStorage
    const storedNotifications =
      JSON.parse(localStorage.getItem('notifications')) || [];

    // Merge notifications, avoiding duplicates
    const existingIds = new Set(storedNotifications.map((n) => n.id));
    const newNotifications = formattedNotifications.filter(
      (n) => !existingIds.has(n.id)
    );

    const updatedNotifications = [...newNotifications, ...storedNotifications];

    // Save to localStorage
    localStorage.setItem('notifications', JSON.stringify(updatedNotifications));

    // Notify listeners
    this.pendingNotificationListeners.forEach((listener) =>
      listener(updatedNotifications)
    );

    // Also update regular notification listeners
    this.notificationListeners.forEach((listener) =>
      listener(updatedNotifications)
    );
  }

  // Handle unread notifications
  handleUnreadNotifications(notifications) {
    // console.log('Unread notifications received:', notifications);

    // Format notifications
    const formattedNotifications = notifications.map((notification) => ({
      ...notification,
      receivedAt: new Date(notification.createdAt || Date.now()),
      read: false
    }));

    // Store in localStorage
    localStorage.setItem(
      'unreadNotifications', // Store in a separate key
      JSON.stringify(formattedNotifications)
    );

    // Notify unread notification listeners
    this.unreadNotificationsListeners.forEach((listener) =>
      listener(formattedNotifications)
    );

    // Also update regular notification listeners
    this.notificationListeners.forEach((listener) =>
      listener(formattedNotifications)
    );
  }

  // Handle read notifications (new method)
  handleReadNotifications(notifications) {
    // console.log('Read notifications received:', notifications);

    // Format notifications
    const formattedNotifications = notifications.map((notification) => ({
      ...notification,
      receivedAt: new Date(notification.createdAt || Date.now()),
      read: true
    }));

    // Store in localStorage
    localStorage.setItem(
      'readNotifications', // Store in a separate key
      JSON.stringify(formattedNotifications)
    );

    // Notify read notification listeners
    this.readNotificationsListeners.forEach((listener) =>
      listener(formattedNotifications)
    );
  }

  // Handle socket errors
  handleSocketError(error) {
    console.error('Socket error:', error);
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }
}

// Create singleton instance
const socketService = new SocketService();
export default socketService;
